import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { MdOpenInFull } from 'react-icons/md';
import NotesEditor from './NotesEditor';

export default function FullscreenNotesEditorOverlay({
  open = false,
  editorRef = null,
  notesValue = '',
  onNotesChange,
  titleValue = '',
  onTitleChange = null,
  placeholder = 'Write your note...',
  onClose,
  inputStyle = {},
  enableInternalLinks = false,
  internalLinkTargets = [],
  internalLinkNotesById = {},
  onInternalLinkNavigate = null,
  onForkNoteRequested = null,
  showForkButton = false,
  toolbarCollapsed = false,
  onToolbarCollapsedChange = null,
}) {
  const [localToolbarCollapsed, setLocalToolbarCollapsed] = useState(false);
  const overlayRef = useRef(null);
  const isCollapseControlled = typeof toolbarCollapsed === 'boolean';
  const isToolbarCollapsed = isCollapseControlled ? toolbarCollapsed : localToolbarCollapsed;
  const setToolbarCollapsed = (nextValue) => {
    const resolved = typeof nextValue === 'function'
      ? nextValue(isToolbarCollapsed)
      : nextValue;
    const next = Boolean(resolved);
    if (!isCollapseControlled) setLocalToolbarCollapsed(next);
    if (typeof onToolbarCollapsedChange === 'function') onToolbarCollapsedChange(next);
  };

  useEffect(() => {
    if (open) return;
    if (!isCollapseControlled) setLocalToolbarCollapsed(false);
  }, [open, isCollapseControlled]);

  // Body background: match overlay bg so keyboard open/close animation doesn't flash underlying page.
  useEffect(() => {
    if (!open) return;
    document.body.classList.add('notes-fullscreen-open');
    return () => document.body.classList.remove('notes-fullscreen-open');
  }, [open]);

  // On mobile, track the visual viewport so the overlay shrinks when the keyboard opens,
  // keeping the bottom toolbar visible above the keyboard / suggestion bar.
  useEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const el = overlayRef.current;
      if (!el) return;
      el.style.height = vv.height + 'px';
      el.style.top = vv.offsetTop + 'px';
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [open]);

  if (!open) return null;

  const controlBtnStyle = {
    fontSize: '0.95rem',
    padding: '4px 9px',
    minWidth: '34px',
    lineHeight: 1,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
  };
  const zenControlBtnStyle = {
    ...controlBtnStyle,
    color: 'color-mix(in srgb, var(--text-primary, #333) 62%, transparent)',
  };
  const shortcutModLabel = (() => {
    if (typeof navigator === 'undefined') return 'Ctrl';
    const platform = navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '';
    return /Mac|iPhone|iPad|iPod/i.test(platform) ? 'Cmd' : 'Ctrl';
  })();
  const titleInputStyle = {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    border: '1px solid color-mix(in srgb, var(--card-border, #ccc) 32%, transparent)',
    borderRadius: '10px',
    outline: 'none',
    background: 'var(--bg-card, #fff)',
    color: 'var(--text-primary, #333)',
    padding: '6px 10px 7px',
  };

  return createPortal(
    <div
      ref={overlayRef}
      className={`fullscreen-notes-overlay${isToolbarCollapsed ? ' is-distraction-free' : ''}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100dvh',
        overflow: 'hidden',
        backgroundColor: 'var(--bg-card, #fff)',
        '--notes-toolbar-bg': 'var(--bg-card, #fff)',
        color: 'var(--text-primary, #333)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: `${isToolbarCollapsed ? '0.5px' : '0'} 0 12px`,
        }}
      >
        {isToolbarCollapsed ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 6px 4px',
              flexShrink: 0,
            }}
          >
            <div
              className="fullscreen-notes-title-input"
              contentEditable
              role="textbox"
              aria-label="Title"
              data-placeholder="Title"
              suppressContentEditableWarning
              onInput={(e) => onTitleChange?.(e.currentTarget.textContent || '')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
              style={titleInputStyle}
            >{typeof titleValue === 'string' ? titleValue : ''}</div>
            <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <button
                type="button"
                className="btn-subtle fullscreen-zen-toggle"
                onMouseDown={(e) => { e.preventDefault(); setToolbarCollapsed((prev) => !prev); }}
                style={zenControlBtnStyle}
                title={`Show toolbar (${shortcutModLabel}+Enter/Esc cycles modes)`}
                aria-label={`Show toolbar (${shortcutModLabel}+Enter/Esc cycles modes)`}
              >
                <MdOpenInFull />
              </button>
              <button
                type="button"
                className="btn-subtle"
                onMouseDown={(e) => { e.preventDefault(); onClose?.(); }}
                style={zenControlBtnStyle}
                title={`Exit fullscreen (Esc, ${shortcutModLabel}+Enter/Esc cycles modes)`}
                aria-label={`Exit fullscreen (Esc, ${shortcutModLabel}+Enter/Esc cycles modes)`}
              >
                <FaTimes />
              </button>
            </div>
          </div>
        ) : null}
        <NotesEditor
          ref={editorRef}
          value={notesValue}
          onChange={onNotesChange}
          placeholder={placeholder}
          hideToolbar={isToolbarCollapsed}
          minimalToolbar={false}
          toolbarExtraRight={!isToolbarCollapsed ? (
            <>
              <button
                type="button"
                className="btn-subtle fullscreen-zen-toggle"
                onMouseDown={(e) => { e.preventDefault(); setToolbarCollapsed((prev) => !prev); }}
                style={controlBtnStyle}
                title={isToolbarCollapsed
                  ? `Show toolbar (${shortcutModLabel}+Enter/Esc cycles modes)`
                  : `Collapse toolbar (${shortcutModLabel}+Enter/Esc cycles modes)`}
                aria-label={isToolbarCollapsed
                  ? `Show toolbar (${shortcutModLabel}+Enter/Esc cycles modes)`
                  : `Collapse toolbar (${shortcutModLabel}+Enter/Esc cycles modes)`}
              >
                <MdOpenInFull />
              </button>
              <button
                type="button"
                className="btn-subtle"
                onMouseDown={(e) => { e.preventDefault(); onClose?.(); }}
                style={controlBtnStyle}
                title={`Exit fullscreen (Esc, ${shortcutModLabel}+Enter/Esc cycles modes)`}
                aria-label={`Exit fullscreen (Esc, ${shortcutModLabel}+Enter/Esc cycles modes)`}
              >
                <FaTimes />
              </button>
            </>
          ) : null}
          headerContent={!isToolbarCollapsed ? (
            <div
              className="fullscreen-notes-title-input"
              contentEditable
              role="textbox"
              aria-label="Title"
              data-placeholder="Title"
              suppressContentEditableWarning
              onInput={(e) => onTitleChange?.(e.currentTarget.textContent || '')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
              style={titleInputStyle}
            >{typeof titleValue === 'string' ? titleValue : ''}</div>
          ) : null}
          enableInternalLinks={enableInternalLinks}
          internalLinkTargets={internalLinkTargets}
          internalLinkNotesById={internalLinkNotesById}
          onInternalLinkNavigate={onInternalLinkNavigate}
          onForkNoteRequested={onForkNoteRequested}
          showForkButton={showForkButton}
        />
      </div>
    </div>,
    document.body
  );
}
